/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation, useParams } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

import ProductDescriptionPageHeader from 'bundles/composable-page-header-consumer/header-renderers/ProductDescriptionPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import PaidMediaFooter from 'bundles/page-footer/components/PaidMediaFooter';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import CoachChatContainer from 'bundles/unified-description-page-common/components/coach/CoachChatContainer';
import TwoPanelConversationalDiscoveryLayout, {
  CHAT_SIDE_BLEED,
  PAGE_GUTTER,
} from 'bundles/unified-description-page-common/components/coach/TwoPanelConversationalDiscoveryLayout';
import { XDP_CHAT_PANEL_WIDTH } from 'bundles/unified-description-page-common/constants';
import PageRefsContext from 'bundles/unified-description-page-common/contexts/PageRefsContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useCoachCareerFitAssistantData } from 'bundles/unified-description-page-common/providers/specialization/CoachCareerFitAssistantProvider';
import { useTwoPanelConversationalDiscoveryExperiment } from 'bundles/unified-description-page-common/providers/specialization/TwoPanelConversationalDiscoveryExperimentProvider';
import { getPageHeaderProps } from 'bundles/unified-description-page-common/utils/getProductHeaderProps';
import isGoogleMarketingPage from 'bundles/unified-description-page-common/utils/isGoogleMarketingPage';
import { isPaidMediaXDP } from 'bundles/unified-description-page-common/utils/isPaidMediaXDP';

// Mirrors the content column when the two-panel chat is open: margin carves out chat width,
// paddings match contentColumn's gutters, bg matches footer's so the gutters aren't
// white strips against the footer's own gray bg.
const footerWrapperStyle = (chatOnLeft: boolean, applyMargin: boolean) =>
  applyMargin
    ? css`
        ${breakpoints.up('md')} {
          ${chatOnLeft ? `margin-left: ${XDP_CHAT_PANEL_WIDTH}px` : `margin-right: ${XDP_CHAT_PANEL_WIDTH}px`};
          padding-left: ${chatOnLeft ? CHAT_SIDE_BLEED : PAGE_GUTTER};
          padding-right: ${chatOnLeft ? PAGE_GUTTER : CHAT_SIDE_BLEED};
          background-color: var(--cds-color-neutral-background-primary-weak);
        }
      `
    : undefined;

const ConsumerHeaderFooterWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const params = useParams();
  const s12nPageData = useSpecializationPageData();
  // Span ref kept for the legacy career-fit chat (CoachChatContainer prop) — preserves prior
  // behavior and the bug fix that introduced this wrapper.
  const legacyHeaderRef = React.useRef<HTMLSpanElement>(null);
  // pageHeaderRef forwards into GlobalPageHeaderContainer's outer .rc-GlobalPageHeaderContainer
  // div (where the smart-header `translateY` is applied) so the layout can measure the
  // transformed rect during the transition. An inline span's rect won't track descendant
  // transforms, which is why we don't reuse legacyHeaderRef here.
  const pageHeaderRef = React.useRef<HTMLDivElement | null>(null);
  // Nav ref is shared via context because FixedPageNavigation is deeply nested in {children}.
  const fixedPageNavRef = React.useRef<HTMLDivElement | null>(null);
  // Stable provider value so consumers don't re-render and effects don't tear down on every
  // re-render of this wrapper. Refs from useRef are stable for the wrapper's lifetime.
  const pageRefs = React.useMemo(() => ({ pageHeaderRef, fixedPageNavRef }), []);
  const { coachCareerFitAssistantEnabled } = useCoachCareerFitAssistantData();
  const { twoPanelConversationalDiscoveryEnabled, chatOnLeft } = useTwoPanelConversationalDiscoveryExperiment();
  const { coachOpen } = useCoachState();
  let productSlug: string | undefined = params.productSlug;

  // Ensure we use the canonical slug if on Google XDP.
  if (isGoogleMarketingPage(location.pathname)) {
    productSlug = s12nPageData?.slug;
  }

  const pageHeaderProps = getPageHeaderProps({ pathname: location.pathname, productSlug });

  const showSimpleHeader = !!isPaidMediaXDP(location.pathname)?.length;

  return (
    <React.Fragment>
      <span ref={legacyHeaderRef}>
        <ProductDescriptionPageHeader
          headerRef={pageHeaderRef}
          getS12nCertificateBannerProps={pageHeaderProps?.getS12nCertificateBannerProps}
          productDiscountPromoBannerProps={
            getPageHeaderProps({ pathname: location.pathname, productSlug })?.productDiscountPromoBannerProps
          }
          showSimpleHeader={showSimpleHeader}
        />
      </span>
      <UniversalPageFeatures />
      {twoPanelConversationalDiscoveryEnabled ? (
        <PageRefsContext.Provider value={pageRefs}>
          <TwoPanelConversationalDiscoveryLayout>
            <main>{children}</main>
          </TwoPanelConversationalDiscoveryLayout>
        </PageRefsContext.Provider>
      ) : (
        <main>{children}</main>
      )}
      {coachCareerFitAssistantEnabled && <CoachChatContainer headerRef={legacyHeaderRef} />}
      <div css={footerWrapperStyle(chatOnLeft, twoPanelConversationalDiscoveryEnabled && coachOpen)}>
        {showSimpleHeader ? <PaidMediaFooter isStickyBarPresent /> : <PageFooter isStickyBarPresent />}
      </div>
    </React.Fragment>
  );
};

export default ConsumerHeaderFooterWrapper;
